import { posthog } from './posthog';

// Types for event tracking
interface ProjectData {
  budget?: number;
  project_type?: string;
  project_id?: string;
  lead_source?: string;
  [key: string]: any;
}

interface LeadData {
  lead_id?: string;
  lead_source?: string;
  lead_status?: string;
  lead_value?: number;
  [key: string]: any;
}

interface NavigationData {
  section: string;
  subsection?: string;
  action?: string;
  [key: string]: any;
}

interface FormData {
  form_type: string;
  success: boolean;
  error_message?: string;
  [key: string]: any;
}

// Project tracking functions
export const trackProjectCreated = (projectData: ProjectData) => {
  posthog.capture('project_created', {
    budget: projectData.budget,
    project_type: projectData.project_type,
    project_id: projectData.project_id,
    lead_source: projectData.lead_source,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};

export const trackProjectUpdated = (projectData: ProjectData) => {
  posthog.capture('project_updated', {
    budget: projectData.budget,
    project_type: projectData.project_type,
    project_id: projectData.project_id,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};

export const trackProjectDeleted = (projectData: ProjectData) => {
  posthog.capture('project_deleted', {
    project_id: projectData.project_id,
    project_type: projectData.project_type,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};

// Lead tracking functions
export const trackLeadCreated = (leadData: LeadData) => {
  posthog.capture('lead_created', {
    lead_id: leadData.lead_id,
    lead_source: leadData.lead_source,
    lead_status: leadData.lead_status,
    lead_value: leadData.lead_value,
    timestamp: new Date().toISOString(),
    ...leadData
  });
};

export const trackLeadUpdated = (leadData: LeadData) => {
  posthog.capture('lead_updated', {
    lead_id: leadData.lead_id,
    lead_source: leadData.lead_source,
    lead_status: leadData.lead_status,
    lead_value: leadData.lead_value,
    timestamp: new Date().toISOString(),
    ...leadData
  });
};

export const trackLeadViewed = (leadData: LeadData) => {
  posthog.capture('lead_viewed', {
    lead_id: leadData.lead_id,
    lead_source: leadData.lead_source,
    lead_status: leadData.lead_status,
    lead_value: leadData.lead_value,
    timestamp: new Date().toISOString(),
    ...leadData
  });
};

export const trackLeadConverted = (leadData: LeadData) => {
  posthog.capture('lead_converted', {
    lead_id: leadData.lead_id,
    lead_source: leadData.lead_source,
    lead_value: leadData.lead_value,
    timestamp: new Date().toISOString(),
    ...leadData
  });
};

// Project Management tracking functions
export const trackProjectManagementAction = (action: string, projectData: ProjectData) => {
  posthog.capture('project_management_action', {
    action,
    project_id: projectData.project_id,
    project_type: projectData.project_type,
    budget: projectData.budget,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};


export const trackTodoCreated = (todoData: any) => {
  posthog.capture('todo_created', {
    timestamp: new Date().toISOString(),
    ...todoData
  });
};

// Checklist tracking functions
export const trackChecklistCreated = (checklistData: any) => {
  posthog.capture('checklist_created', {
    timestamp: new Date().toISOString(),
    ...checklistData
  });
};

export const trackChecklistUpdated = (checklistData: any) => {
  posthog.capture('checklist_updated', {
    timestamp: new Date().toISOString(),
    ...checklistData
  });
};

export const trackChecklistViewed = (checklistData: any) => {
  posthog.capture('checklist_viewed', {
    timestamp: new Date().toISOString(),
    ...checklistData
  });
};

// Bill tracking functions
export const trackBillCreated = (billData: any) => {
  posthog.capture('bill_created', {
    timestamp: new Date().toISOString(),
    ...billData
  });
};

export const trackBillUpdated = (billData: any) => {
  posthog.capture('bill_updated', {
    timestamp: new Date().toISOString(),
    ...billData
  });
};

// Invoice tracking functions
export const trackInvoiceUpdated = (invoiceData: any) => {
  posthog.capture('invoice_updated', {
    timestamp: new Date().toISOString(),
    ...invoiceData
  });
};

// Payment tracking functions
export const trackInvoicePaymentReceived = (paymentData: any) => {
  posthog.capture('invoice_payment_received', {
    timestamp: new Date().toISOString(),
    ...paymentData
  });
};

export const trackInvoicePaymentUpdated = (paymentData: any) => {
  posthog.capture('invoice_payment_updated', {
    timestamp: new Date().toISOString(),
    ...paymentData
  });
};

export const trackBillPaymentReceived = (paymentData: any) => {
  posthog.capture('bill_payment_received', {
    timestamp: new Date().toISOString(),
    ...paymentData
  });
};

export const trackBillPaymentUpdated = (paymentData: any) => {
  posthog.capture('bill_payment_updated', {
    timestamp: new Date().toISOString(),
    ...paymentData
  });
};

export const trackMaterialOrderCreated = (projectData: ProjectData) => {
  posthog.capture('material_order_created', {
    project_id: projectData.project_id,
    project_type: projectData.project_type,
    budget: projectData.budget,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};

export const trackScheduleUpdated = (projectData: ProjectData) => {
  posthog.capture('schedule_updated', {
    project_id: projectData.project_id,
    project_type: projectData.project_type,
    timestamp: new Date().toISOString(),
    ...projectData
  });
};

// Navigation tracking functions
export const trackNavigation = (navigationData: NavigationData) => {
  posthog.capture('navigation_used', {
    timestamp: new Date().toISOString(),
    ...navigationData
  });
};

export const trackSidebarClick = (section: string, subsection?: string) => {
  trackNavigation({
    section,
    subsection,
    action: 'sidebar_click'
  });
};

// Form tracking functions
export const trackFormSubmission = (formData: FormData) => {
  posthog.capture('form_submitted', {
    timestamp: new Date().toISOString(),
    ...formData
  });
};

export const trackFormError = (formType: string, errorMessage: string) => {
  trackFormSubmission({
    form_type: formType,
    success: false,
    error_message: errorMessage
  });
};

// Button and interaction tracking
export const trackButtonClick = (buttonName: string, context?: any) => {
  posthog.capture('button_clicked', {
    button_name: buttonName,
    context,
    timestamp: new Date().toISOString()
  });
};

export const trackFileUpload = (fileType: string, fileSize?: number, context?: any) => {
  posthog.capture('file_uploaded', {
    file_type: fileType,
    file_size: fileSize,
    context,
    timestamp: new Date().toISOString()
  });
};

export const trackFileDownload = (fileType: string, fileName?: string, context?: any) => {
  posthog.capture('file_downloaded', {
    file_type: fileType,
    file_name: fileName,
    context,
    timestamp: new Date().toISOString()
  });
};

// Cost Management tracking
export const trackCostManagementAction = (action: string, data: any) => {
  posthog.capture('cost_management_action', {
    action,
    timestamp: new Date().toISOString(),
    ...data
  });
};

export const trackInvoiceCreated = (invoiceData: any) => {
  posthog.capture('invoice_created', {
    invoice_amount: invoiceData.amount,
    invoice_type: invoiceData.type,
    project_id: invoiceData.project_id,
    timestamp: new Date().toISOString(),
    ...invoiceData
  });
};

// Messages and Communication tracking
export const trackMessageSent = (messageData: any) => {
  posthog.capture('message_sent', {
    message_type: messageData.type,
    recipient_type: messageData.recipient_type,
    project_id: messageData.project_id,
    timestamp: new Date().toISOString(),
    ...messageData
  });
};

export const trackCommentAdded = (commentData: any) => {
  posthog.capture('comment_added', {
    comment_type: commentData.type,
    project_id: commentData.project_id,
    timestamp: new Date().toISOString(),
    ...commentData
  });
};

// AI Chat tracking
export const trackAIChatUsed = (chatData: any) => {
  posthog.capture('ai_chat_used', {
    chat_type: chatData.type,
    query_length: chatData.query?.length,
    timestamp: new Date().toISOString(),
    ...chatData
  });
};

// Generic tracking function for any custom event
export const trackCustomEvent = (eventName: string, properties?: any) => {
  posthog.capture(eventName, {
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Daily Logs tracking functions
export const trackDailyLogCreated = (logData: any) => {
  posthog.capture('daily_log_created', {
    timestamp: new Date().toISOString(),
    ...logData
  });
};

export const trackDailyLogUpdated = (logData: any) => {
  posthog.capture('daily_log_updated', {
    timestamp: new Date().toISOString(),
    ...logData
  });
};

export const trackDailyLogViewed = (logData: any) => {
  posthog.capture('daily_log_viewed', {
    timestamp: new Date().toISOString(),
    ...logData
  });
};

// Schedule tracking functions
export const trackScheduleViewed = (scheduleData: any) => {
  posthog.capture('schedule_viewed', {
    timestamp: new Date().toISOString(),
    ...scheduleData
  });
};

export const trackScheduleTaskCreated = (taskData: any) => {
  posthog.capture('schedule_task_created', {
    timestamp: new Date().toISOString(),
    ...taskData
  });
};

export const trackScheduleTaskUpdated = (taskData: any) => {
  posthog.capture('schedule_task_updated', {
    timestamp: new Date().toISOString(),
    ...taskData
  });
};

// Drive/File Management tracking functions
export const trackFileUploaded = (fileData: any) => {
  posthog.capture('file_uploaded', {
    timestamp: new Date().toISOString(),
    ...fileData
  });
};

export const trackFileDownloaded = (fileData: any) => {
  posthog.capture('file_downloaded', {
    timestamp: new Date().toISOString(),
    ...fileData
  });
};

export const trackFileDeleted = (fileData: any) => {
  posthog.capture('file_deleted', {
    timestamp: new Date().toISOString(),
    ...fileData
  });
};

export const trackFolderCreated = (folderData: any) => {
  posthog.capture('folder_created', {
    timestamp: new Date().toISOString(),
    ...folderData
  });
};

export const trackDriveNavigation = (navigationData: any) => {
  posthog.capture('drive_navigation', {
    timestamp: new Date().toISOString(),
    ...navigationData
  });
};

// User Management tracking functions
export const trackUserCreated = (userData: any) => {
  posthog.capture('user_created', {
    timestamp: new Date().toISOString(),
    ...userData
  });
};

export const trackUserUpdated = (userData: any) => {
  posthog.capture('user_updated', {
    timestamp: new Date().toISOString(),
    ...userData
  });
};

export const trackUserDeleted = (userData: any) => {
  posthog.capture('user_deleted', {
    timestamp: new Date().toISOString(),
    ...userData
  });
};

export const trackTeamMemberAdded = (memberData: any) => {
  posthog.capture('team_member_added', {
    timestamp: new Date().toISOString(),
    ...memberData
  });
};

export const trackTeamMemberRemoved = (memberData: any) => {
  posthog.capture('team_member_removed', {
    timestamp: new Date().toISOString(),
    ...memberData
  });
};

// Client tracking functions
export const trackClientCreated = (clientData: any) => {
  posthog.capture('client_created', {
    timestamp: new Date().toISOString(),
    ...clientData
  });
};

export const trackClientUpdated = (clientData: any) => {
  posthog.capture('client_updated', {
    timestamp: new Date().toISOString(),
    ...clientData
  });
};

export const trackClientDeleted = (clientData: any) => {
  posthog.capture('client_deleted', {
    timestamp: new Date().toISOString(),
    ...clientData
  });
};

// Vendor tracking functions
export const trackVendorCreated = (vendorData: any) => {
  posthog.capture('vendor_created', {
    timestamp: new Date().toISOString(),
    ...vendorData
  });
};

export const trackVendorUpdated = (vendorData: any) => {
  posthog.capture('vendor_updated', {
    timestamp: new Date().toISOString(),
    ...vendorData
  });
};

export const trackVendorDeleted = (vendorData: any) => {
  posthog.capture('vendor_deleted', {
    timestamp: new Date().toISOString(),
    ...vendorData
  });
};

// Time Tracking functions
export const trackTimeEntryCreated = (timeData: any) => {
  posthog.capture('time_entry_created', {
    timestamp: new Date().toISOString(),
    ...timeData
  });
};

export const trackTimeEntryUpdated = (timeData: any) => {
  posthog.capture('time_entry_updated', {
    timestamp: new Date().toISOString(),
    ...timeData
  });
};

export const trackTimeEntryDeleted = (timeData: any) => {
  posthog.capture('time_entry_deleted', {
    timestamp: new Date().toISOString(),
    ...timeData
  });
};

// Change Orders functions
export const trackChangeOrderCreated = (changeOrderData: any) => {
  posthog.capture('change_order_created', {
    timestamp: new Date().toISOString(),
    ...changeOrderData
  });
};

export const trackChangeOrderUpdated = (changeOrderData: any) => {
  posthog.capture('change_order_updated', {
    timestamp: new Date().toISOString(),
    ...changeOrderData
  });
};

export const trackChangeOrderApproved = (changeOrderData: any) => {
  posthog.capture('change_order_approved', {
    timestamp: new Date().toISOString(),
    ...changeOrderData
  });
};

export const trackChangeOrderRejected = (changeOrderData: any) => {
  posthog.capture('change_order_rejected', {
    timestamp: new Date().toISOString(),
    ...changeOrderData
  });
};

// Specifications functions
export const trackSpecificationCreated = (specData: any) => {
  posthog.capture('specification_created', {
    timestamp: new Date().toISOString(),
    ...specData
  });
};

export const trackSpecificationUpdated = (specData: any) => {
  posthog.capture('specification_updated', {
    timestamp: new Date().toISOString(),
    ...specData
  });
};

export const trackSpecificationViewed = (specData: any) => {
  posthog.capture('specification_viewed', {
    timestamp: new Date().toISOString(),
    ...specData
  });
};

// Bids functions
export const trackBidCreated = (bidData: any) => {
  posthog.capture('bid_created', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

export const trackBidUpdated = (bidData: any) => {
  posthog.capture('bid_updated', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

export const trackBidSubmitted = (bidData: any) => {
  posthog.capture('bid_submitted', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

export const trackBidWon = (bidData: any) => {
  posthog.capture('bid_won', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

export const trackBidLost = (bidData: any) => {
  posthog.capture('bid_lost', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

export const trackBidDeleted = (bidData: any) => {
  posthog.capture('bid_deleted', {
    timestamp: new Date().toISOString(),
    ...bidData
  });
};

// Estimates functions
export const trackEstimateCreated = (estimateData: any) => {
  posthog.capture('estimate_created', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

export const trackEstimateUpdated = (estimateData: any) => {
  posthog.capture('estimate_updated', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

export const trackEstimateSent = (estimateData: any) => {
  posthog.capture('estimate_sent', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

export const trackEstimateAccepted = (estimateData: any) => {
  posthog.capture('estimate_accepted', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

export const trackEstimateRejected = (estimateData: any) => {
  posthog.capture('estimate_rejected', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

export const trackEstimateDeleted = (estimateData: any) => {
  posthog.capture('estimate_deleted', {
    timestamp: new Date().toISOString(),
    ...estimateData
  });
};

// Budget tracking functions
export const trackBudgetCreated = (budgetData: any) => {
  posthog.capture('budget_created', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};

export const trackBudgetUpdated = (budgetData: any) => {
  posthog.capture('budget_updated', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};

export const trackBudgetSent = (budgetData: any) => {
  posthog.capture('budget_sent', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};

export const trackBudgetAccepted = (budgetData: any) => {
  posthog.capture('budget_accepted', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};

export const trackBudgetRejected = (budgetData: any) => {
  posthog.capture('budget_rejected', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};

export const trackBudgetDeleted = (budgetData: any) => {
  posthog.capture('budget_deleted', {
    timestamp: new Date().toISOString(),
    ...budgetData
  });
};